"use client"

import * as React from "react"
import { Notebook, Calculator, CloudSun, Settings, User, ShoppingBag, Bot, Building2, Calendar, FileText, Globe, Layout, Shield } from "lucide-react"

export function AppIcon({
  iconUrl,
  iframeUrl,
  name = "App",
  size = "large",
  customSizePx,
}: {
  iconUrl?: string
  iframeUrl?: string
  name?: string
  size?: "small" | "medium" | "large" | "xlarge"
  customSizePx?: number
}) {
  const [errorCount, setErrorCount] = React.useState(0)
  const safeName = typeof name === "string" ? name : "App"

  const noBg = iconUrl?.includes("nobg=true")

  let containerClass = ""
  let iconSizeClass = ""
  let customStyle: React.CSSProperties | undefined = undefined

  if (customSizePx) {
    customStyle = {
      width: `${customSizePx}px`,
      height: `${customSizePx}px`,
    }
    const isSmall = customSizePx < 44
    containerClass = `${isSmall ? "rounded-xl" : "rounded-2xl"} flex items-center justify-center shrink-0 ${noBg ? "" : "shadow-sm border-2 border-white/90 dark:border-zinc-700/80 bg-gradient-to-tr from-[#004EB1] to-[#0066d6] dark:from-blue-600 dark:to-indigo-600"
      }`
    iconSizeClass = ""
  } else if (size === "xlarge") {
    containerClass = `size-20 rounded-3xl mb-3 flex items-center justify-center shrink-0 ${noBg ? "" : "shadow-md border-2 border-white/90 dark:border-zinc-700/80 bg-gradient-to-tr from-[#004EB1] to-[#0066d6] dark:from-blue-600 dark:to-indigo-600"
      }`
    iconSizeClass = "size-10"
  } else if (size === "large") {
    containerClass = `size-16 rounded-2xl mb-3 flex items-center justify-center shrink-0 ${noBg ? "" : "shadow-sm border-2 border-white/90 dark:border-zinc-700/80 bg-gradient-to-tr from-[#004EB1] to-[#0066d6] dark:from-blue-600 dark:to-indigo-600"
      }`
    iconSizeClass = "size-7"
  } else if (size === "medium") {
    containerClass = `size-9 rounded-md flex items-center justify-center shrink-0 ${noBg ? "" : "border-2 border-white/90 dark:border-zinc-700/80 bg-gradient-to-tr from-[#004EB1] to-[#0066d6] dark:from-blue-600 dark:to-indigo-600"
      }`
    iconSizeClass = "size-5.5"
  } else {
    containerClass = `size-4.5 rounded-sm flex items-center justify-center shrink-0 ${noBg ? "" : "border-2 border-white/90 dark:border-zinc-700/80 bg-gradient-to-tr from-[#004EB1] to-[#0066d6] dark:from-blue-600 dark:to-indigo-600"
      }`
    iconSizeClass = "size-3.5"
  }

  const iconPixelSize = customSizePx ? Math.round(customSizePx * 0.52) : undefined
  const iconStyle: React.CSSProperties | undefined = iconPixelSize ? { width: `${iconPixelSize}px`, height: `${iconPixelSize}px` } : undefined

  const isEmoji = !!(iconUrl && (iconUrl.startsWith("emoji:") || (!iconUrl.startsWith("http") && !iconUrl.startsWith("/") && iconUrl.length <= 4)))
  const emoji = isEmoji ? (iconUrl!.startsWith("emoji:") ? iconUrl!.split(":")[1] : iconUrl!) : ""
  const lowerName = safeName.toLowerCase()

  // Detect known system apps (Loja, Perfil, Configurações, etc.)
  const isProfile = emoji === "👤" || lowerName.includes("perfil") || lowerName.includes("profile")
  const isMarketplace = emoji === "🛒" || emoji === "🛍️" || emoji === "🛍" || lowerName.includes("market") || lowerName.includes("loja") || lowerName.includes("tienda") || lowerName.includes("app store")
  const isSettings = emoji === "⚙️" || lowerName.includes("ajuste") || lowerName.includes("config") || lowerName.includes("setting")
  const isNotes = emoji === "📝" || emoji === "📓" || lowerName.includes("nota")
  const isCalc = emoji === "🧮" || lowerName.includes("calculadora")
  const isWeather = emoji === "⛅" || emoji === "☀️" || lowerName.includes("clima")
  const isCalendar = emoji === "📅" || lowerName.includes("calend")
  const isBot = emoji === "🤖" || lowerName.includes("agent") || lowerName.includes("ia")

  const isSystemApp = isProfile || isMarketplace || isSettings || isNotes || isCalc || isWeather || isCalendar || isBot

  const renderSystemIcon = () => {
    let iconEl: React.ReactNode = null
    if (isNotes) iconEl = <Notebook className={`${iconSizeClass} text-amber-500 dark:text-amber-400`} style={iconStyle} />
    else if (isCalc) iconEl = <Calculator className={`${iconSizeClass} text-purple-500 dark:text-purple-400`} style={iconStyle} />
    else if (isWeather) iconEl = <CloudSun className={`${iconSizeClass} text-sky-500 dark:text-sky-400`} style={iconStyle} />
    else if (isCalendar) iconEl = <Calendar className={`${iconSizeClass} text-rose-500 dark:text-rose-400`} style={iconStyle} />
    else if (isSettings) iconEl = <Settings className={`${iconSizeClass} text-blue-500 dark:text-blue-400`} style={iconStyle} />
    else if (isProfile) iconEl = <User className={`${iconSizeClass} text-white`} style={iconStyle} />
    else if (isMarketplace) iconEl = <ShoppingBag className={`${iconSizeClass} text-white`} style={iconStyle} />
    else if (isBot) iconEl = <Bot className={`${iconSizeClass} text-blue-500 dark:text-blue-400`} style={iconStyle} />

    if (!iconEl) return null

    const gradientClass = customSizePx
      ? "bg-white dark:bg-zinc-850 shadow-sm border border-zinc-200/60 dark:border-zinc-750"
      : size === "xlarge" || size === "large"
        ? "bg-white dark:bg-zinc-850 shadow-sm border border-zinc-200/60 dark:border-zinc-750"
        : "bg-white dark:bg-zinc-850 border border-zinc-200/60 dark:border-zinc-750"

    return (
      <div className={`${containerClass} ${gradientClass} select-none`} style={customStyle}>
        {iconEl}
      </div>
    )
  }

  // 1. Detect if it is an emoji or known system app
  if (!iconUrl || isEmoji || !iconUrl.startsWith("http")) {
    if (isSystemApp) {
      return renderSystemIcon()
    }

    if (isEmoji && emoji) {
      const gradientClass = customSizePx
        ? "bg-gradient-to-tr from-blue-500/10 to-indigo-500/10 dark:from-blue-500/25 dark:to-indigo-500/25"
        : size === "xlarge"
          ? "bg-gradient-to-tr from-blue-500/15 to-indigo-500/15 dark:from-blue-500/30 dark:to-indigo-500/30"
          : size === "large"
            ? "bg-gradient-to-tr from-blue-500/10 to-indigo-500/10 dark:from-blue-500/25 dark:to-indigo-500/25"
            : size === "medium"
              ? "bg-gradient-to-tr from-blue-500/5 to-indigo-500/5 dark:from-blue-500/15 dark:to-indigo-500/15"
              : "bg-zinc-100 dark:bg-zinc-800"
      const emojiSize = customSizePx
        ? undefined
        : size === "xlarge" ? "text-4xl" : size === "large" ? "text-3xl" : size === "medium" ? "text-lg" : "text-[10px]"

      return (
        <div className={`${containerClass} ${gradientClass} select-none`} style={customStyle}>
          <span className={`${emojiSize || ""}`} style={customSizePx ? { fontSize: `${Math.round(customSizePx * 0.5)}px` } : undefined}>{emoji}</span>
        </div>
      )
    }
  }

  // 2. Try normal URL
  let parsedUrl: URL | null = null
  if (iframeUrl) {
    let urlStr = iframeUrl.trim()
    if (!urlStr.startsWith("http://") && !urlStr.startsWith("https://") && !urlStr.startsWith("/")) {
      urlStr = urlStr.startsWith("localhost") ? "http://" + urlStr : "https://" + urlStr
    }
    try {
      parsedUrl = new URL(urlStr)
    } catch (e) { }
  }

  const candidates: string[] = []
  if (iconUrl) {
    candidates.push(iconUrl)
  }

  const finalSrc = candidates[errorCount] || ""
  const handleImageError = () => {
    setErrorCount((prev) => prev + 1)
  }

  // 3. Elegant default icon on error or missing image
  if (errorCount >= candidates.length || !finalSrc) {
    if (isSystemApp) {
      return renderSystemIcon()
    }

    const firstLetter = safeName.trim().charAt(0).toUpperCase() || "B"
    return (
      <div
        className={`${containerClass} overflow-hidden relative shadow-md border-2 border-white/90 dark:border-zinc-700/80 bg-gradient-to-tr from-[#004EB1] to-[#0066d6] dark:from-blue-600 dark:to-indigo-600 flex items-center justify-center select-none`}
        style={customStyle}
      >
        {/* Subtle accent glow line */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(8,243,108,0.25),transparent_70%)]" />
        <span
          className={`font-black tracking-tighter text-white font-sans ${!customSizePx ? (size === "xlarge" ? "text-3xl" : size === "large" ? "text-2xl" : size === "medium" ? "text-sm" : "text-[9px]") : ""
            }`}
          style={customSizePx ? { fontSize: `${Math.round(customSizePx * 0.45)}px` } : undefined}
        >
          {firstLetter}
        </span>
      </div>
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={finalSrc}
      alt={safeName}
      draggable={false}
      onError={handleImageError}
      style={customStyle}
      className={`${containerClass} object-cover pointer-events-none select-none ${noBg ? "" : "p-0.5"}`}
    />
  )
}
